import {
  ApiResponse,
  CreateManpowerLogData,
  ManpowerLogEntry,
  ManpowerLogQuery,
  ManpowerLogStats,
  PayrollData,
  Project,
} from '../types';
import api from './api';

class ManpowerLogService {
  // Get manpower logs with filtering and pagination
  async getManpowerLogs(query: ManpowerLogQuery = {}): Promise<
    ApiResponse<{
      entries: ManpowerLogEntry[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  > {
    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/manpower-logs?${params.toString()}`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error('Failed to fetch manpower logs:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load manpower logs',
      };
    }
  }

  // Get single manpower log by ID
  async getManpowerLog(logId: string): Promise<ApiResponse<ManpowerLogEntry>> {
    try {
      const response = await api.get(`/manpower-logs/${logId}`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error('Failed to fetch manpower log:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load manpower log',
      };
    }
  }

  // Create new manpower log
  async createManpowerLog(data: CreateManpowerLogData): Promise<ApiResponse<ManpowerLogEntry>> {
    try {
      const response = await api.post('/manpower-logs', data);
      return {
        success: true,
        data: response.data.data,
        message: 'Manpower log created successfully',
      };
    } catch (error: any) {
      console.error('Failed to create manpower log:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create manpower log',
      };
    }
  }

  // Update manpower log
  async updateManpowerLog(
    logId: string,
    data: Partial<CreateManpowerLogData>
  ): Promise<ApiResponse<ManpowerLogEntry>> {
    try {
      const response = await api.put(`/manpower-logs/${logId}`, data);
      return {
        success: true,
        data: response.data.data,
        message: 'Manpower log updated successfully',
      };
    } catch (error: any) {
      console.error('Failed to update manpower log:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update manpower log',
      };
    }
  }

  // Delete manpower log
  async deleteManpowerLog(logId: string): Promise<ApiResponse<null>> {
    try {
      await api.delete(`/manpower-logs/${logId}`);
      return {
        success: true,
        data: null,
        message: 'Manpower log deleted successfully',
      };
    } catch (error: any) {
      console.error('Failed to delete manpower log:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete manpower log',
      };
    }
  }

  // Get manpower statistics for a project
  async getManpowerStats(
    projectId: string,
    dateRange?: { from: string; to: string }
  ): Promise<ApiResponse<ManpowerLogStats>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.from) params.append('date_from', dateRange.from);
      if (dateRange?.to) params.append('date_to', dateRange.to);

      const response = await api.get(
        `/manpower-logs/projects/${projectId}/stats?${params.toString()}`
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error('Failed to fetch manpower stats:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load manpower statistics',
      };
    }
  }

  // Get payroll data for a project
  async getPayrollData(
    projectId: string,
    dateRange: { from: string; to: string },
    status = 'approved'
  ): Promise<ApiResponse<PayrollData>> {
    try {
      const params = new URLSearchParams({
        date_from: dateRange.from,
        date_to: dateRange.to,
        status,
      });

      const response = await api.get(
        `/manpower-logs/projects/${projectId}/payroll?${params.toString()}`
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error('Failed to fetch payroll data:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load payroll data',
      };
    }
  }

  // Bulk update status of multiple logs
  async bulkUpdateStatus(
    entryIds: string[],
    status: 'approved' | 'paid' | 'cancelled',
    notes?: string
  ): Promise<ApiResponse<{ modified: number }>> {
    try {
      const response = await api.patch('/manpower-logs/bulk-status', {
        entry_ids: entryIds,
        status,
        notes,
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      console.error('Failed to bulk update status:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update status',
      };
    }
  }

  // Add comment to manpower log
  async addComment(logId: string, text: string): Promise<ApiResponse<ManpowerLogEntry>> {
    try {
      const response = await api.post(`/manpower-logs/${logId}/comments`, { text });
      return {
        success: true,
        data: response.data.data,
        message: 'Comment added successfully',
      };
    } catch (error: any) {
      console.error('Failed to add comment:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add comment',
      };
    }
  }

  // Add adjustment to manpower log
  async addAdjustment(
    logId: string,
    amount: number,
    reason: string
  ): Promise<ApiResponse<ManpowerLogEntry>> {
    try {
      const response = await api.post(`/manpower-logs/${logId}/adjustments`, { amount, reason });
      return {
        success: true,
        data: response.data.data,
        message: 'Adjustment added successfully',
      };
    } catch (error: any) {
      console.error('Failed to add adjustment:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add adjustment',
      };
    }
  }

  // Get projects for dropdown
  async getProjectsForDropdown(): Promise<{ value: string; label: string }[]> {
    try {
      const response = await api.get('/projects');
      const responseData = response.data.data || response.data;
      return (responseData || []).map((project: Project) => ({
        value: project.id,
        label: project.name,
      }));
    } catch (error) {
      console.error('Failed to fetch projects for dropdown:', error);
      return [];
    }
  }

  // Get companies for dropdown
  async getCompaniesForDropdown(): Promise<{ value: string; label: string }[]> {
    try {
      const response = await api.get('/manpower/companies');
      const responseData = response.data.data || response.data;
      return (responseData || []).map((company: any) => ({
        value: company.id,
        label: company.name,
      }));
    } catch (error) {
      console.error('Failed to fetch companies for dropdown:', error);
      return [];
    }
  }

  // Get worker roles from configuration
  async getWorkerRoles(): Promise<{ value: string; label: string }[]> {
    try {
      const response = await api.get('/configurations/dropdown/worker-roles');
      const responseData = response.data.data || response.data;
      return responseData || this.getDefaultWorkerRoles();
    } catch (error) {
      console.error('Failed to fetch worker roles:', error);
      return this.getDefaultWorkerRoles();
    }
  }

  // Get default worker roles (fallback)
  private getDefaultWorkerRoles(): { value: string; label: string }[] {
    return [
      { value: 'electrician', label: 'Electrician' },
      { value: 'plumber', label: 'Plumber' },
      { value: 'carpenter', label: 'Carpenter' },
      { value: 'mason', label: 'Mason' },
      { value: 'painter', label: 'Painter' },
      { value: 'welder', label: 'Welder' },
      { value: 'hvac_technician', label: 'HVAC Technician' },
      { value: 'roofer', label: 'Roofer' },
      { value: 'flooring_installer', label: 'Flooring Installer' },
      { value: 'drywall_installer', label: 'Drywall Installer' },
      { value: 'tile_setter', label: 'Tile Setter' },
      { value: 'landscaper', label: 'Landscaper' },
      { value: 'demolition_worker', label: 'Demolition Worker' },
      { value: 'crane_operator', label: 'Crane Operator' },
      { value: 'forklift_operator', label: 'Forklift Operator' },
      { value: 'safety_officer', label: 'Safety Officer' },
      { value: 'site_supervisor', label: 'Site Supervisor' },
      { value: 'project_manager', label: 'Project Manager' },
      { value: 'architect', label: 'Architect' },
      { value: 'engineer', label: 'Engineer' },
      { value: 'general_laborer', label: 'General Laborer' },
      { value: 'other', label: 'Other' },
    ];
  }

  // Status options
  getStatusOptions(): { value: string; label: string }[] {
    return [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'paid', label: 'Paid' },
      { value: 'cancelled', label: 'Cancelled' },
    ];
  }

  // Utility functions
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  formatHours(hours: number): string {
    if (hours === 1) return '1 hour';
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    return `${hours.toFixed(1)} hours`;
  }

  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-red-100 text-red-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  calculateTotalPayment(data: CreateManpowerLogData): number {
    const regularPay = data.number_of_workers * data.hours_worked * data.hourly_rate;
    const overtimePay = data.number_of_workers * data.overtime_hours * data.overtime_rate;
    return regularPay + overtimePay;
  }

  validateLogData(data: CreateManpowerLogData): string[] {
    const errors: string[] = [];

    if (!data.project_id) errors.push('Project is required');
    if (!data.role || data.role.trim() === '') errors.push('Role is required');
    if (!data.location?.site || data.location.site.trim() === '')
      errors.push('Location site is required');
    if (!data.date) errors.push('Date is required');
    if (data.number_of_workers < 1) errors.push('Number of workers must be at least 1');
    if (data.hours_worked < 0) errors.push('Hours worked cannot be negative');
    if (data.hours_worked > 24) errors.push('Hours worked cannot exceed 24 hours per day');
    if (data.overtime_hours < 0) errors.push('Overtime hours cannot be negative');
    if (data.overtime_hours > 12) errors.push('Overtime hours cannot exceed 12 hours per day');
    if (data.hourly_rate < 0) errors.push('Hourly rate cannot be negative');
    if (data.overtime_rate < 0) errors.push('Overtime rate cannot be negative');

    // Either worker or company details required
    const hasWorker = data.worker_id && data.worker_name;
    const hasCompany = data.company_id && data.company_name;

    if (!hasWorker && !hasCompany) {
      errors.push('Either worker details or company details must be provided');
    }

    if (hasWorker && hasCompany) {
      errors.push('Cannot specify both worker and company details');
    }

    // GPS validation if provided
    if (data.location.gps && data.location.gps.trim() !== '') {
      const gpsRegex = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/;
      if (!gpsRegex.test(data.location.gps)) {
        errors.push('GPS coordinates must be in format "latitude, longitude"');
      }
    }

    return errors;
  }
}

export default new ManpowerLogService();
